import React from 'react';

import { UseFloatingReturn } from '@floating-ui/react-dom/src/types';

import { HTMLElementEvent, SetIsVisible } from './useClick';

interface ContextProps {
  isVisible: boolean;
  setIsVisible: SetIsVisible;
  reference: ({ ...DOMRect }) => void;
  refs: UseFloatingReturn['refs'];
}
export const useContext = ({
  refs,
  reference,
  isVisible,
  setIsVisible,
}: ContextProps) => {
  // used to keep track of the original reference value when closing the context
  const originalReference = React.useRef<null | Element>(null);
  React.useEffect(() => {
    originalReference.current = refs.reference.current as Element;
  }, [refs.reference]);
  // click manager
  const handleContextMenu = React.useCallback(
    (event: HTMLElementEvent<HTMLElement>) => {
      if (!refs.reference?.current) return;
      if (!originalReference.current?.contains(event.target)) {
        // context menu outside the menu
        setIsVisible(false);
        return;
      }
      // lock the window
      event.preventDefault();
      reference({
        getBoundingClientRect() {
          return {
            width: 0,
            height: 0,
            x: event.clientX,
            y: event.clientY,
            top: event.clientY,
            left: event.clientX,
            right: event.clientX,
            bottom: event.clientY,
          };
        },
      });
      setIsVisible((state) => !state);
    },
    [reference, refs.reference, setIsVisible]
  );
  const contextClickHandler = React.useCallback(
    (event: MouseEvent) => {
      if (!refs.reference?.current) return;
      const target = event.target as HTMLElement;
      if (!refs.floating.current?.contains(target)) {
        setIsVisible(false);
        return;
      }
      if (!refs.floating.current?.contains(target)) {
        setIsVisible(false);
      }
    },
    [refs.floating, refs.reference, setIsVisible]
  );
  const toggleBodyOverflow = React.useCallback(() => {
    if (isVisible) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [isVisible]);
  const contextManager = () => {
    window.addEventListener('click', contextClickHandler);
    window.addEventListener('contextmenu', handleContextMenu);
    toggleBodyOverflow();
    window.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsVisible(false);
    });
    return () => {
      window.removeEventListener('click', contextClickHandler);
      window.removeEventListener('contextmenu', handleContextMenu);
      window.removeEventListener('keydown', (e: KeyboardEvent) => {
        if (e.key === 'Escape') setIsVisible(false);
      });
    };
  };
  return { contextManager };
};
